export interface BoardConfig {
  busStop: string[];
  isLightTheme: boolean;
  maxDepartures: number;
  showStand: boolean;
  showStopName: boolean;
  showLastUpdated: boolean;
  directionFilter: string | boolean;
}

type ParameterMap = Record<string, Record<string, any>>;

const parameterList: ParameterMap = {
  busstop: {
    default: ['LANCBS001', 'Lancaster Bus Station'],
    underpass: ['2500918', 'Lancaster University Underpass'],
    commongardenstreet: ['LANCBS002', 'Common Garden Street'],
    southernperimeter: ['2500915', 'InfoLab (Southern perimeter)'],
    busstation: ['LANCBS001', 'Lancaster Bus Station'],
    nightstand: ['2500DCL3092', 'Lancaster Bus Station Night Stand'],
  },
  theme: {
    default: false,
    light: true,
    dark: false
  },
  showstand: {
    default: false,
    true: true,
    false: false
  },
  showstopname: {
    default: false,
    true: true,
    false: false
  },
  showlastupdated: {
    default: false,
    true: true,
    false: false
  },
  direction: {
    default: false,
    south: 'south',
    north: 'north'
  },
  maxdepartures: {
    default: 100
  }
};

export function normalizeBoardParams(params: Record<string, any>): Record<string, any> {
  return Object.keys(params).reduce((carry, key) => {
    carry[key.toLowerCase().trim()] = params[key];
    return carry;
  }, {} as Record<string, any>);
}

export function resolveBoardConfig(
  params: Record<string, any>,
  suffix = '',
  defaults: Partial<Record<string, any>> = {}
): BoardConfig {
  const normalizedParams = normalizeBoardParams(params);

  return {
    busStop: resolveParamValue('busstop', normalizedParams, suffix, defaults),
    showStand: resolveParamValue('showstand', normalizedParams, suffix, defaults),
    isLightTheme: resolveParamValue('theme', normalizedParams, suffix, defaults),
    directionFilter: resolveParamValue('direction', normalizedParams, suffix, defaults),
    maxDepartures: resolveParamValue('maxdepartures', normalizedParams, suffix, defaults),
    showStopName: resolveParamValue('showstopname', normalizedParams, suffix, defaults),
    showLastUpdated: resolveParamValue('showlastupdated', normalizedParams, suffix, defaults)
  };
}

function resolveParamValue(
  key: string,
  params: Record<string, any>,
  suffix: string,
  defaults: Partial<Record<string, any>>
) {
  const paramKey = `${key}${suffix}`.toLowerCase();
  const parameterOptions = parameterList[key];
  const fallbackValue = resolveFallbackValue(key, defaults[key], parameterOptions);

  if (!(paramKey in params)) {
    return fallbackValue;
  }

  const paramValue = `${params[paramKey]}`.trim();
  if (paramValue in parameterOptions) {
    return parameterOptions[paramValue];
  }

  if (key === 'maxdepartures' && !isNaN(+paramValue) && parseInt(paramValue, 10) > 0) {
    return parseInt(paramValue, 10);
  }

  return fallbackValue;
}

function resolveFallbackValue(
  key: string,
  fallback: any,
  parameterOptions: Record<string, any>
) {
  if (fallback === undefined) {
    return parameterOptions.default;
  }

  const normalizedFallback = `${fallback}`.trim();
  if (normalizedFallback in parameterOptions) {
    return parameterOptions[normalizedFallback];
  }

  if (key === 'maxdepartures' && !isNaN(+normalizedFallback) && parseInt(normalizedFallback, 10) > 0) {
    return parseInt(normalizedFallback, 10);
  }

  return fallback;
}
